import React, { useState, useRef } from 'react';
import { Search, Loader2, User, Briefcase, X } from 'lucide-react';
import { Dialog } from './Dialog';
import { Button } from './Button';
import eventService from '../services/eventService';
import { CRMPerson } from '../types/event';

// Busca de CONVIDADOS no CRM (clientes + personal organizers/parceiras comerciais),
// via gRPC do crmbackgo — padrão visual do CustomerSearchModal, fonte diferente.

const formatDocument = (doc?: string) => {
  if (!doc) return '';
  const d = doc.replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return doc;
};

interface GuestSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (person: CRMPerson) => void;
}

export const GuestSearchModal: React.FC<GuestSearchModalProps> = ({ isOpen, onClose, onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CRMPerson[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const handleSearch = (value: string) => {
    setQuery(value);
    setError(null);
    clearTimeout(debounceRef.current);
    if (!value.trim()) {
      setResults([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await eventService.searchGuests(value.trim());
        setResults(data);
      } catch (e) {
        // CRM fora do ar ≠ "convidado não encontrado": mostra o erro.
        setResults([]);
        setError(e instanceof Error ? e.message : 'Falha ao consultar o CRM');
      } finally {
        setLoading(false);
        setSearched(true);
      }
    }, 350);
  };

  const resetAndClose = () => {
    setQuery('');
    setResults([]);
    setSearched(false);
    setLoading(false);
    setError(null);
    onClose();
  };

  const handleSelect = (person: CRMPerson) => {
    onSelect(person);
    resetAndClose();
  };

  return (
    <Dialog isOpen={isOpen} onClose={resetAndClose} title="Buscar Convidado no CRM" size="lg">
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            className="w-full input-field pl-9 pr-8 py-2.5 text-sm"
            placeholder="Pesquisar clientes e personal organizers por nome, CPF/CNPJ, e-mail ou telefone..."
            value={query}
            onChange={e => handleSearch(e.target.value)}
            autoFocus
          />
          {query && (
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              onClick={() => { setQuery(''); setResults([]); setSearched(false); setError(null); }}
            >
              <X size={14} />
            </button>
          )}
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
        )}

        <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-slate-400 text-sm">
              <Loader2 size={16} className="animate-spin" /> Buscando no CRM...
            </div>
          ) : results.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {results.map(person => (
                <button
                  key={`${person.kind}-${person.id}`}
                  type="button"
                  className="w-full text-left px-4 py-3 hover:bg-emerald-50 transition-colors"
                  onClick={() => handleSelect(person)}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 p-1.5 bg-slate-100 rounded-full">
                      {person.kind === 'commercial_partner'
                        ? <Briefcase size={14} className="text-violet-500" />
                        : <User size={14} className="text-slate-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900 text-sm truncate">{person.name || '—'}</p>
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                          person.kind === 'commercial_partner'
                            ? 'bg-violet-100 text-violet-700'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {person.kind === 'commercial_partner' ? 'Personal Organizer' : 'Cliente'}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
                        {person.document && <span className="text-xs text-slate-500">{formatDocument(person.document)}</span>}
                        {person.email && <span className="text-xs text-slate-500">{person.email}</span>}
                        {person.cell_phone && <span className="text-xs text-slate-500">{person.cell_phone}</span>}
                      </div>
                      {(person.addr_street || person.addr_city) && (
                        <p className="text-xs text-slate-400 mt-0.5 truncate">
                          {person.addr_street}
                          {person.addr_num ? `, ${person.addr_num}` : ''}
                          {person.addr_city ? ` — ${person.addr_city}` : ''}
                          {person.addr_state ? `/${person.addr_state}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : searched && !error ? (
            <div className="py-8 text-center text-slate-400 text-sm">
              Nenhum convidado encontrado para "{query}"
            </div>
          ) : !error ? (
            <div className="py-8 text-center text-slate-400 text-sm">
              Digite para buscar clientes e personal organizers do CRM
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-slate-200 mt-4">
        <Button variant="outline" onClick={resetAndClose}>Fechar</Button>
      </div>
    </Dialog>
  );
};
